import {
  Component,
  Inject,
  OnInit, PLATFORM_ID,
  Renderer2
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet
} from '@angular/router';
import {AppModule} from "./app.module";
import {DOCUMENT, isPlatformBrowser, registerLocaleData} from "@angular/common";
import localeIt from '@angular/common/locales/it';
import localeEnGb from '@angular/common/locales/en-GB';
import localeHr from '@angular/common/locales/hr';
import localeSlo from '@angular/common/locales/sl';
import localeSrLatn from '@angular/common/locales/sr-Latn';
import localeSr from '@angular/common/locales/sr';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import {NgbDatepickerConfig} from "@ng-bootstrap/ng-bootstrap";
import {LANG, LoaderService} from "@services";
import {AbstractComponent} from "@components";
import {Meta} from "@angular/platform-browser";
import {environment} from "@environment";
import {overrideDateToJSONMethodToSerializeDateToLocalDateTime, overrideJSONParseToDeserializeDate} from "@utility";
import {TranslocoService} from "@ngneat/transloco";
import {GeolocationService} from "./services/geolocation.service";
import {AVAILABLE_LANGUAGES} from "./app.config";

declare var gtag: Function;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, AppModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent extends AbstractComponent implements OnInit {
  isAdmin: boolean = false;
  isBrowser: boolean;

  constructor(private ngbDatepickerConfig: NgbDatepickerConfig, private router: Router, private renderer: Renderer2,
              private loaderService: LoaderService, @Inject(DOCUMENT) private document: Document,
              private metaService: Meta, @Inject(PLATFORM_ID) private platformId: Object,
              private geolocationService: GeolocationService, private translocoService: TranslocoService) {
    super();
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    overrideDateToJSONMethodToSerializeDateToLocalDateTime();
    overrideJSONParseToDeserializeDate();
    this.setGoogleSiteVerificationToken();
    this.registerLocaleData();
    this.setDefaultLang();
    this.initDatepickerComponent();
    this.subscriptions.push(this.router.events.subscribe((routerEvent: any) => {
        this.navigationInterceptor(routerEvent);
        this.isAdmin = this.getCurrentUrl().includes("admin");
        if (this.isAdmin)
          this.renderer.addClass(this.document.body, 'admin-mode');
        else
          this.renderer.removeClass(this.document.body, 'admin-mode');
      }
    ));
  }

// Shows and hides the loading spinner during RouterEvent changes
  navigationInterceptor(event: any): void {
    if (event instanceof NavigationStart) {
      if (this.loaderService.consoleOutputEnabled) {
        console.log("NavigationStart.startLoading");
      }
      this.loaderService.startLoading();
    }
    if (event instanceof NavigationEnd) {
      if (this.loaderService.consoleOutputEnabled) {
        console.log("NavigationEnd.dismissLoading");
      }
      try {
        if (this.isBrowser) {
          // Google Analytics page view tracking
          gtag('event', 'page_view', {
            page_path: event.urlAfterRedirects
          });
        }
      } catch (e) {
        console.error("Error in gtag page_view tracking:", e);
      }
      this.loaderService.dismissLoading();
    }
    if (event instanceof NavigationCancel) {
      if (this.loaderService.consoleOutputEnabled) {
        console.log("NavigationCancel.dismissLoading");
      }
      this.loaderService.dismissLoading();
    }
    if (event instanceof NavigationError) {
      if (this.loaderService.consoleOutputEnabled) {
        console.log("NavigationError.dismissLoading");
      }
      this.loaderService.dismissLoading();
    }
  }

  private initDatepickerComponent() {
    let currentDate: Date = new Date();
    this.ngbDatepickerConfig.minDate = {year: 1910, month: 1, day: 1};
    this.ngbDatepickerConfig.maxDate = {
      year: currentDate.getFullYear(),
      month: currentDate.getMonth() + 1,
      day: currentDate.getDate()
    };
  }

  private registerLocaleData() {
    registerLocaleData(localeIt, 'it');
    registerLocaleData(localeEnGb, 'en-GB');
    registerLocaleData(localeHr, 'hr');
    registerLocaleData(localeSlo, 'slo');
    registerLocaleData(localeSr, 'sr');
    registerLocaleData(localeSr, 'bs');
    registerLocaleData(localeSrLatn, 'sr-Latn');
    registerLocaleData(localeDe, 'de');
    registerLocaleData(localeFr, 'fr');
  }

  private setDefaultLang(): void {
    const defaultLang = 'en';

    if (typeof window !== 'undefined') {
      const storedLang = localStorage.getItem(LANG);
      if (storedLang && AVAILABLE_LANGUAGES.includes(storedLang)) {
        this.translocoService.setActiveLang(storedLang);
        return;
      }

      let browserLangSet = false;
      if (window.navigator) {
        const browserLanguages = window.navigator.languages || [window.navigator.language];
        for (const browserLang of browserLanguages) {
          const langCode = browserLang.toLowerCase().split('-')[0];
          let mappedLang: string | null = null;

          switch (langCode) {
            case 'hr':
              mappedLang = 'hr';
              break;
            case 'en':
              mappedLang = 'en';
              break;
            case 'it':
              mappedLang = 'it';
              break;
            case 'sl':
              mappedLang = 'slo';
              break;
            case 'sr':
              mappedLang = 'sr';
              break;
            case 'bs':
              mappedLang = 'bs';
              break;
            case 'de':
              mappedLang = 'de';
              break;
            case 'fr':
              mappedLang = 'fr';
              break;
          }

          if (mappedLang && AVAILABLE_LANGUAGES.includes(mappedLang)) {
            this.translocoService.setActiveLang(mappedLang);
            localStorage.setItem(LANG, mappedLang);
            browserLangSet = true;
            return;
          }
        }
      }

      if (!browserLangSet) {
        if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const longitude = position.coords.longitude;
            const latitude = position.coords.latitude;

            this.subscriptions.push(
              this.geolocationService.getLocationInfo(latitude, longitude).subscribe({
                next: (locationData) => {

                  if (locationData && locationData.address.country_code) {
                    localStorage.setItem(LANG, locationData.address.country_code);
                    this.translocoService.setActiveLang(locationData.address.country_code);
                  }
                },
                error: (error) => {
                  this.translocoService.setActiveLang(defaultLang);
                }
              })
            );
          },
          (error) => {
            this.translocoService.setActiveLang(defaultLang);
          }
        );
      } else {
        this.translocoService.setActiveLang(defaultLang);
      }
    }
      }
  }

  getCurrentUrl(): string {
    return this.document.location.href;
  }

  setGoogleSiteVerificationToken() {
    this.metaService.addTag({name: 'google-site-verification', content: environment.google_site_verification});
  }
}
